import React, { createContext, FC, ReactNode, useContext } from 'react'

/**
 * Context for extension-level features that cards can optionally consume.
 * This allows cards to access extension functionality without violating
 * architectural boundaries (e.g., /features importing from /pages).
 */
export interface IndicationDetailCardExtensionContext {
  bottomPanelContainer: HTMLDivElement | null
  requestAuxiliaryBottomPanel: (id: string) => void
  releaseAuxiliaryBottomPanel: (id: string) => void
  /**
   * Optional: zoom/fit the host's asset visualization to a region by id (e.g. a
   * ruler/markup id). Provided by hosts that have a viz pane (libra FFS
   * workspace); absent elsewhere, in which case cards should hide zoom controls.
   */
  zoomToRegion?: (regionId: string) => void
}

const ExtensionContext = createContext<
  IndicationDetailCardExtensionContext | undefined
>(undefined)

export const IndicationDetailCardExtensionProvider: FC<{
  children: ReactNode
  value?: IndicationDetailCardExtensionContext
}> = ({ children, value }) => {
  return (
    <ExtensionContext.Provider value={value}>
      {children}
    </ExtensionContext.Provider>
  )
}

/**
 * Hook for cards to access extension features (like bottom panel).
 * Returns undefined if no extension context is provided.
 */
export const useIndicationDetailCardExtension = () => {
  return useContext(ExtensionContext)
}
